import { stringify } from 'qs';
import {
    HubConnection,
    HubConnectionBuilder,
    LogLevel,
} from '@microsoft/signalr';
import Languages from '../../../locale/language-enum';
import {
    INotificationsResponse,
    ISystem,
} from '../../components/custom-header/components/notifications/Interfaces/INotificationsResponse';
import IPagination from '../../interfaces/IPagination';
import { getNotificationAxiosInstance } from '../axios-config';
import { INotificationCounter } from '../../components/custom-header/components/notifications/Interfaces/INotificationCounter';
import IApiResponse from '../../interfaces/IApiResponse';
import i18n from '../../../lib/i18n';
import ConfigurationManager from '../../../utils/configurationManager';

const getNotificationsSystems = (isThirdUser: boolean) =>
    isThirdUser ? [ISystem.SmartCargas, ISystem.SulaHub] : [ISystem.SulaHub];

const getAllNotificationsPaginated = async (
    isThirdUser: boolean,
    totalNotifications: number,
): Promise<IApiResponse<IPagination<INotificationsResponse>>> => {
    const size = 10;
    const request = {
        _page: 1,
        _size: size,
        language:
            Languages[
                localStorage.getItem(
                    'UserLanguageSelected',
                ) as keyof typeof Languages
            ],
        system: getNotificationsSystems(isThirdUser),
    };

    const Api = await getNotificationAxiosInstance();
    const responseData: IPagination<INotificationsResponse> = {
        currentPage: 0,
        data: [] as INotificationsResponse[],
        totalItems: 0,
        totalPages: 1,
    } as IPagination<INotificationsResponse>;

    try {
        const promises = [];
        for (let i = 0; i < Math.ceil(totalNotifications / size); i++) {
            promises.push(
                Api.get('v1/notifications:paginated', {
                    params: request,
                    paramsSerializer(params) {
                        return stringify(params, { allowDots: true });
                    },
                }),
            );

            request._page++;
        }

        const responses = await Promise.all(promises);

        responses.forEach(({ data }) => {
            responseData.data =
                data.data?.length > 0
                    ? [...responseData.data, ...data.data]
                    : responseData.data;
            responseData.currentPage = data.currentPage;
            responseData.totalPages = data.totalPages;
            responseData.totalItems = data.totalItems;
        });
    } catch {
        return {
            error: new Error(i18n.t('Notifications.PaginatedError')),
        };
    }

    return { response: responseData };
};

const getNotificationsCounter = async (
    isThirdUser: boolean,
): Promise<IApiResponse<INotificationCounter>> => {
    const request = {
        system: getNotificationsSystems(isThirdUser),
    };

    const Api = await getNotificationAxiosInstance();

    try {
        const response = await Api.get('v1/notifications:count', {
            params: request,
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        if (response.data) {
            const { data } = response;
            return { response: data };
        }

        return { response: {} as INotificationCounter };
    } catch {
        return {
            error: new Error(i18n.t('Notifications.CounterError')),
        };
    }
};

const markAsRead = async (
    notificationId: string,
): Promise<IApiResponse<boolean>> => {
    const Api = await getNotificationAxiosInstance();

    try {
        await Api.post(`v1/notifications/${notificationId}:read`);
        return { response: true };
    } catch {
        return {
            error: new Error(i18n.t('Notifications.ReadError')),
        };
    }
};

const markAllAsRead = async (
    isThirdUser: boolean,
): Promise<IApiResponse<boolean>> => {
    const Api = await getNotificationAxiosInstance();

    try {
        await Api.post('v1/notifications:read-all', {
            system: getNotificationsSystems(isThirdUser),
        });

        return { response: true };
    } catch {
        return {
            error: new Error(i18n.t('Notifications.ReadAllError')),
        };
    }
};

const buildConnectionUrl = (
    baseUrl: string,
    group: string,
    sulaHubId?: string,
): string => {
    const params = new URLSearchParams();
    params.append('group', group);

    if (sulaHubId) {
        params.append('sulaHubId', sulaHubId);
    }

    return `${baseUrl}/v1/notifications/ws?${params.toString()}`;
};

const getConnectionManager = async (
    group: string,
    sulaHubId?: string,
): Promise<HubConnection> => {
    const { OCP_APIM_SUBSCRIPTION_KEY, API_NOTIFICATION_WEB_SOCKET } =
        await ConfigurationManager.getInstance().getConfigFile();

    const connectionUrl = buildConnectionUrl(
        API_NOTIFICATION_WEB_SOCKET,
        group,
        sulaHubId,
    );

    const connection = new HubConnectionBuilder()
        .withUrl(connectionUrl, {
            skipNegotiation: false,
            accessTokenFactory: () =>
                sessionStorage.getItem('AccessToken') ?? '',
            headers: {
                'Ocp-Apim-Subscription-Key': OCP_APIM_SUBSCRIPTION_KEY,
            },
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

    return connection;
};

const HeaderService = {
    getAllNotificationsPaginated,
    getNotificationsCounter,
    markAsRead,
    markAllAsRead,
    getConnectionManager,
};

export default HeaderService;
